<script lang="ts">
import type { ThemeSlots } from '#layers/ui/types/tv';
import type { ColorPresetInput } from '#layers/ui/utils/colors';
import { tv } from 'tailwind-variants';

const colorSwatches = tv({
	slots: {
		root: 'flex flex-wrap items-center gap-2',
		swatch: `
			flex size-6 shrink-0 items-center justify-center rounded-full border border-(--swatch-border) transition-all
			hover:scale-110 hover:ring-2
			focus:outline-none
			focus-visible:scale-110 focus-visible:ring-1 focus-visible:ring-primary
		`,
	},
});

export interface ColorSwatchesProps {
	presets?: ColorPresetInput[];
	disabled?: boolean;
	ui?: ThemeSlots<typeof colorSwatches>;
}
</script>

<script lang="ts" setup>
import type { AcceptableValue } from 'reka-ui';
import { createReusableTemplate } from '@vueuse/core';
import { RadioGroupItem, RadioGroupRoot } from 'reka-ui';
import { computed } from 'vue';

import { useAppConfig } from '#app/config';
import { DEFAULT_COLORS, getBorderColor, getColorInfo, normalizeColorPreset } from '#layers/ui/utils/colors';

const props = withDefaults(defineProps<ColorSwatchesProps>(), {
	presets: () => [...DEFAULT_COLORS],
});

const slots = defineSlots<{
	trailing?: () => unknown;
}>();
const modelValue = defineModel<string | null>();
const appConfig = useAppConfig();
const normalizedPresets = computed(() => props.presets.map((preset) => normalizeColorPreset(preset)));
const [DefineSwatch, ReuseSwatch] = createReusableTemplate<{
	preset: ReturnType<typeof normalizeColorPreset>;
}>();

function swatchStyle(color?: string | null) {
	return {
		'backgroundColor': color,
		'--tw-ring-color': color ? `color-mix(in srgb, ${color} 30%, transparent)` : undefined,
		'--swatch-border': getBorderColor(color) ?? 'transparent',
	};
}

function handleSelect(color: AcceptableValue) {
	if (typeof color !== 'string') return;

	modelValue.value = color;
}

function resolveTheme() {
	return colorSwatches();
}
</script>

<template>
	<RadioGroupRoot
		:class="resolveTheme().root({ class: [props.ui?.root] })"
		:disabled="props.disabled"
		:model-value
		@update:model-value="handleSelect"
	>
		<DefineSwatch v-slot="{ preset }">
			<RadioGroupItem
				v-slot="{ checked }"
				:value="preset.value"
				:disabled="props.disabled"
				:aria-label="preset.label"
				:class="resolveTheme().swatch({ class: [props.ui?.swatch, getColorInfo(preset.color).isLight ? 'text-dark' : 'text-white'] })"
				:style="swatchStyle(preset.color)"
			>
				<DIcon v-if="checked" :name="appConfig.ui.icons.check" class="size-3" />
			</RadioGroupItem>
		</DefineSwatch>

		<template v-for="(preset, index) in normalizedPresets" :key="`${preset.value}-${index}`">
			<DTooltip v-if="preset.tooltip" :text="preset.tooltip" :delay-duration="0">
				<ReuseSwatch :preset />
			</DTooltip>
			<ReuseSwatch v-else :preset />
		</template>
		<slot v-if="slots.trailing" name="trailing" />
	</RadioGroupRoot>
</template>
